import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import { parseBlockHeader, parsePersonPricesLine } from './blockHelpers';
import {
  getCell,
  getGridColumnCount,
  parsePastedGrid,
} from './parsePastedGrid';
import {
  findDuplicateLabels,
  normalizeTariffName,
  splitSideBySideBlocks,
  tagColumn,
} from './multiTariffPaste';
import { findOverlapsWithOtherTariffs } from './parsePastedPeriods';
import type {
  ChildBandsCount,
  ComparableTariff,
  ExcelPasteIssue,
  ExcelPasteResult,
  PastedPeriod,
  PastedPersonPrices,
} from './types';

export const SUPPLEMENT_BLOCK_ROWS = 13;
export const SUPPLEMENT_BLOCK_COLUMNS = 3;
export const SUPPLEMENT_LABEL_LINE = 5;
export const SUPPLEMENT_PRICE_LINE = 13;

/** Lines 1 to 4 of a supplement block: "Concerne" a pension type when marked. */
export const SUPPLEMENT_PENSION_LINES: Array<{
  line: number;
  pensionType: Exclude<HotelPensionType, 'SingleRoom'>;
}> = [
  { line: 1, pensionType: 'Breakfast' },
  { line: 2, pensionType: 'SemiPension' },
  { line: 3, pensionType: 'FullPension' },
  { line: 4, pensionType: 'AllInclusive' },
];

export interface PastedSupplementTariff {
  /** Pension types the supplement applies to, they replace the selection. */
  pensionTypes: HotelPensionType[];
  name: string;
  periods: PastedPeriod[];
  pricing: PastedPersonPrices;
}

export interface SupplementPasteContext {
  childBandsCount: ChildBandsCount;
  /** Pension types the page offers for supplements (the base and above). */
  offeredPensionTypes: HotelPensionType[];
  /** The base pension (`pricingPensionType.value`), for messages. */
  basePensionType?: string;
  /** The other tariffs of the same supplement. */
  otherTariffs: ComparableTariff[];
}

/**
 * Reads a 13×3 festive supplement block (grid V3): the pension types it
 * concerns (any mark in column 1, "x" in the grids), the label, 7 periods and
 * the per-person price.
 */
export function parseSupplementTariffBlock(
  text: string,
  {
    childBandsCount,
    offeredPensionTypes,
    basePensionType,
    otherTariffs,
  }: SupplementPasteContext,
): ExcelPasteResult<PastedSupplementTariff> {
  const rows = parsePastedGrid(text);
  if (
    rows.length !== SUPPLEMENT_BLOCK_ROWS ||
    getGridColumnCount(rows) > SUPPLEMENT_BLOCK_COLUMNS
  ) {
    return {
      value: null,
      errors: [{ code: 'supplementShape', params: { received: rows.length } }],
      warnings: [],
    };
  }

  const result = parseSupplementTariffRows(rows, {
    childBandsCount,
    offeredPensionTypes,
    basePensionType,
  });
  if (!result.value) return result;
  return {
    ...result,
    warnings: findOverlapsWithOtherTariffs(result.value.periods, otherTariffs),
  };
}

/** A tariff of a side-by-side paste, with the supplement it belongs to. */
export type PastedNamedSupplementTariff = PastedSupplementTariff & {
  supplementName: string;
  /**
   * True when the block had no name line: the name is the tariff label
   * without its final year, and an existing supplement keeps its own.
   */
  isNameDerived: boolean;
};

/**
 * The label without a final year or season ("Christmas Gala Dinner 2026",
 * "Noël 2026-2027" → "Christmas Gala Dinner", "Noël"): what the tariffs of
 * one supplement have in common in the grids.
 */
export const stripFinalYear = (label: string) =>
  label.replace(/\s*\(?\d{4}(\s*[-–/]\s*\d{2,4})?\)?\s*$/, '').trim() ||
  label.trim();

/** Key grouping a supplement's tariffs and matching an existing supplement. */
export const supplementKey = (name?: string | null) =>
  normalizeTariffName(stripFinalYear(name ?? ''));

/**
 * Every festive supplement tariff at once: blocks side by side, 3 columns
 * per tariff, empty slots skipped. Each block is the usual 13-line block,
 * optionally under a line naming its supplement (14 lines). Without that
 * line, tariffs are shared out by label without the final year, as the grids
 * name them ("Christmas Gala Dinner 2026", "Christmas Gala Dinner 2027").
 * Tariffs of one supplement must agree on the pension types.
 */
export function parseSupplementTariffsBlock(
  text: string,
  context: Omit<SupplementPasteContext, 'otherTariffs'>,
): ExcelPasteResult<PastedNamedSupplementTariff[]> {
  const rows = parsePastedGrid(text);
  const hasNameLine = rows.length === SUPPLEMENT_BLOCK_ROWS + 1;
  if (rows.length !== SUPPLEMENT_BLOCK_ROWS && !hasNameLine) {
    return {
      value: null,
      errors: [
        { code: 'supplementTariffsShape', params: { received: rows.length } },
      ],
      warnings: [],
    };
  }
  const columnCount = getGridColumnCount(rows);
  if (columnCount % SUPPLEMENT_BLOCK_COLUMNS !== 0) {
    return {
      value: null,
      errors: [
        {
          code: 'tariffColumns',
          params: { received: columnCount, width: SUPPLEMENT_BLOCK_COLUMNS },
        },
      ],
      warnings: [],
    };
  }

  const errors: ExcelPasteIssue[] = [];
  const tariffs: PastedNamedSupplementTariff[] = [];
  splitSideBySideBlocks(rows, SUPPLEMENT_BLOCK_COLUMNS).forEach(
    ({ inColumn, rows: blockRows }) => {
      const namedName = hasNameLine ? getCell(blockRows, 1, 0) : null;
      const blockErrors: ExcelPasteIssue[] =
        namedName === ''
          ? [{ code: 'missingSupplementName', params: { line: 1 } }]
          : [];
      // Below a name line, the usual block's line numbers are shifted by one
      // in messages to match what was copied.
      const shift = hasNameLine ? 1 : 0;
      const parsed = parseSupplementTariffRows(blockRows.slice(shift), context);
      blockErrors.push(
        ...parsed.errors.map((issue) =>
          typeof issue.params?.line === 'number'
            ? {
                ...issue,
                params: { ...issue.params, line: issue.params.line + shift },
              }
            : issue,
        ),
      );
      errors.push(...tagColumn(blockErrors, inColumn));
      if (parsed.value && blockErrors.length === 0) {
        tariffs.push({
          ...parsed.value,
          supplementName: namedName ?? stripFinalYear(parsed.value.name),
          isNameDerived: namedName === null,
        });
      }
    },
  );
  if (errors.length === 0 && tariffs.length === 0) {
    errors.push({ code: 'noCategoryTariff' });
  }

  groupBySupplement(tariffs).forEach(({ supplementName, tariffs: group }) => {
    errors.push(...findDuplicateLabels(group.map(({ name }) => name)));
    const pensionTypes = group[0].pensionTypes.join(',');
    if (
      group.some((tariff) => tariff.pensionTypes.join(',') !== pensionTypes)
    ) {
      errors.push({
        code: 'supplementPensionTypesDiffer',
        params: { supplement: supplementName },
      });
    }
  });

  if (errors.length > 0) return { value: null, errors, warnings: [] };
  return { value: tariffs, errors: [], warnings: [] };
}

/** Pasted tariffs grouped by supplement name, in paste order. */
export function groupBySupplement(
  tariffs: PastedNamedSupplementTariff[],
): Array<{
  supplementName: string;
  isNameDerived: boolean;
  tariffs: PastedNamedSupplementTariff[];
}> {
  const groups = new Map<
    string,
    {
      supplementName: string;
      isNameDerived: boolean;
      tariffs: PastedNamedSupplementTariff[];
    }
  >();
  tariffs.forEach((tariff) => {
    const key = supplementKey(tariff.supplementName);
    const group = groups.get(key);
    if (group) group.tariffs.push(tariff);
    else {
      groups.set(key, {
        supplementName: tariff.supplementName,
        isNameDerived: tariff.isNameDerived,
        tariffs: [tariff],
      });
    }
  });
  return [...groups.values()];
}

export interface ExistingSupplement {
  name?: string | null;
  tariffNames: Array<string | null | undefined>;
  isBlank: boolean;
}

/**
 * Where each group of pasted tariffs goes: the supplement whose name, or one
 * of whose tariff labels, has the same key (both without their final year,
 * since a supplement pasted alone is named after its first tariff), else the
 * next blank supplement, else a new one.
 */
export function matchPastedSupplements<G extends { supplementName: string }>(
  groups: G[],
  existing: ExistingSupplement[],
): { targets: Array<{ supplementIndex: number; group: G }>; newCount: number } {
  const used = new Set<number>();
  const keysOf = ({ name, tariffNames }: ExistingSupplement) =>
    new Set(
      [name, ...tariffNames].map(supplementKey).filter((key) => key !== ''),
    );
  const byKey = groups.map((group) => {
    const key = supplementKey(group.supplementName);
    const index = existing.findIndex(
      (supplement, position) =>
        !used.has(position) && keysOf(supplement).has(key),
    );
    if (index >= 0) used.add(index);
    return index;
  });

  let newCount = 0;
  const targets = groups.map((group, position) => {
    let supplementIndex = byKey[position];
    if (supplementIndex < 0) {
      supplementIndex = existing.findIndex(
        ({ isBlank }, index) => isBlank && !used.has(index),
      );
    }
    if (supplementIndex < 0) {
      supplementIndex = existing.length + newCount;
      newCount += 1;
    }
    used.add(supplementIndex);
    return { supplementIndex, group };
  });
  return { targets, newCount };
}

/** One 13×3 block already split into cells. */
function parseSupplementTariffRows(
  rows: string[][],
  {
    childBandsCount,
    offeredPensionTypes,
    basePensionType,
  }: Omit<SupplementPasteContext, 'otherTariffs'>,
): ExcelPasteResult<PastedSupplementTariff> {
  const errors: ExcelPasteIssue[] = [];
  const pensionTypes = SUPPLEMENT_PENSION_LINES.filter(
    ({ line }) => getCell(rows, line, 0) !== '',
  ).map(({ line, pensionType }) => {
    if (!offeredPensionTypes.includes(pensionType)) {
      errors.push({
        code: 'supplementPensionNotOffered',
        params: { line, base: basePensionType ?? '', pensionType },
      });
    }
    return pensionType;
  });
  if (pensionTypes.length === 0) {
    errors.push({ code: 'noSupplementPensionType' });
  }

  const header = parseBlockHeader(rows, SUPPLEMENT_LABEL_LINE);
  errors.push(...header.errors);
  const pricing = parsePersonPricesLine(
    rows,
    SUPPLEMENT_PRICE_LINE,
    childBandsCount,
    errors,
  );

  if (errors.length > 0) return { value: null, errors, warnings: [] };

  return {
    value: {
      pensionTypes,
      name: header.name,
      periods: header.periods,
      pricing,
    },
    errors: [],
    warnings: [],
  };
}
